// src/components/Navbar.js
import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const SparkleIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2L9.5 9.5 2 12l7.5 2.5L12 22l2.5-7.5L22 12l-7.5-2.5z"/>
  </svg>
);

const Navbar = () => {
  const { user, logout }  = useAuth();
  const navigate          = useNavigate();
  const location          = useLocation();
  const dropdownRef       = useRef(null);

  const [solid,    setSolid]    = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 16);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setDropOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => { setMenuOpen(false); setDropOpen(false); }, [location]);

  const handleLogout = async () => { await logout(); navigate("/"); };
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + "/");

  return (
    <nav className={`navbar${solid || menuOpen ? " navbar--solid" : ""}`}>
      <div className="navbar__inner">
        {/* Logo */}
        <Link to="/" className="navbar__logo">
          <span className="logo-mark">HI</span>
          <span className="logo-name">Halro Images</span>
        </Link>

        {/* Desktop links */}
        <div className="navbar__links">
          <Link to="/" className={`navbar__link${isActive("/") && location.pathname === "/" ? " navbar__link--active" : ""}`}>
            Discover
          </Link>
          <Link to="/categories" className={`navbar__link${isActive("/categories") || isActive("/category") ? " navbar__link--active" : ""}`}>
            Categories
          </Link>
          <Link to="/generate" className={`navbar__link navbar__link--ai${isActive("/generate") ? " navbar__link--active" : ""}`}>
            <SparkleIcon /> AI Generate
          </Link>
          {user && (
            <Link to="/downloads" className={`navbar__link${isActive("/downloads") ? " navbar__link--active" : ""}`}>
              My Downloads
            </Link>
          )}
        </div>

        {/* Desktop actions */}
        <div className="navbar__actions">
          {user ? (
            <div className="user-menu" ref={dropdownRef}>
              <button className="user-menu__trigger"
                onClick={() => setDropOpen(v => !v)}
                aria-label="Account menu" aria-expanded={dropOpen}>
                {user.photoURL ? (
                  <img src={user.photoURL} alt={user.displayName || "User"} className="avatar" />
                ) : (
                  <div className="avatar avatar--initial">
                    {(user.displayName || user.email || "U")[0].toUpperCase()}
                  </div>
                )}
              </button>
              {dropOpen && (
                <div className="user-menu__dropdown">
                  <div className="dropdown__header">
                    <p className="dropdown__name">{user.displayName || "User"}</p>
                    <p className="dropdown__email">{user.email}</p>
                  </div>
                  <Link to="/downloads"  className="dropdown__item">My Downloads</Link>
                  <Link to="/categories" className="dropdown__item">Categories</Link>
                  <Link to="/generate"   className="dropdown__item">AI Generate</Link>
                  <button className="dropdown__item dropdown__item--danger" onClick={handleLogout}>
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/auth" className="btn-primary btn-sm">Sign In</Link>
          )}
        </div>

        {/* Hamburger */}
        <button
          className={`hamburger${menuOpen ? " hamburger--open" : ""}`}
          onClick={() => setMenuOpen(v => !v)}
          aria-label="Toggle menu" aria-expanded={menuOpen}>
          <span /><span /><span />
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="mobile-nav">
          <Link to="/"           className="mobile-nav__link">Discover</Link>
          <Link to="/categories" className="mobile-nav__link">📂 Categories</Link>
          <Link to="/generate"   className="mobile-nav__link mobile-nav__link--cta">✦ AI Generate</Link>
          {user && <Link to="/downloads" className="mobile-nav__link">My Downloads</Link>}
          <div className="mobile-nav__divider" />
          {user ? (
            <>
              <div style={{ padding: "10px 24px 4px", fontSize: 13, color: "var(--text-2)" }}>
                {user.displayName || user.email}
              </div>
              <button className="mobile-nav__link mobile-nav__link--danger" onClick={handleLogout}>
                Sign Out
              </button>
            </>
          ) : (
            <Link to="/auth" className="mobile-nav__link mobile-nav__link--cta">Sign In</Link>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navbar;
